import React from "react";
import { Form, Label } from "semantic-ui-react";
import { useFormContext } from "react-hook-form";
import useLogin from "@common/components/hooks/useLogin";
import { useCharacterCount } from "../../hooks/useCharacterCount";

/**
 * TextareaComponent renders a textarea field with validation.
 *
 * @typedef {object} InputProps
 * @property {string} name - Name of the input field.
 * @property {string} [label=''] - Label of the input field.
 * @property {string} placeholder - Placeholder text of the input field.
 * @property {string} rows - Rows number of the input field.
 * @property {Function} register - Function to register the input field for form
 * @property {Object} [error=null] - Object containing validation error.
 * @property {boolean} [labelAbove=false] - Boolean to determine if the label is above or next to the textarea.
 * @property {string} [labelStyle=''] - Style for the label.
 * @property {string} [fieldStyle=''] - Style for Form.Field.
 * @property {string} [textareaContainerStyle='w-100'] - Style for textarea container.
 * @property {number} [rows=5] - Number of rows the textarea has.
 * @property {number} [maxLength=750] - Maximum number of characters allowed.
 *
 * @component
 * @param {InputProps} props - The component props.
 * @returns {JSX.Element} The rendered textarea input field with validation.
 */
export default function TextareaComponent({
  name,
  label = "",
  placeholder,
  register,
  error = null,
  labelAbove = false,
  labelStyle = "",
  fieldStyle = "",
  textareaContainerStyle = "w-100",
  rows = 5,
  maxLength = 750,
  showCharacterCount = true,
}) {
  const [{ isInReadMode }] = useLogin();
  const { watch } = useFormContext(); // Get watch function from form context

  // Watch the specific field value to react to changes including form reset
  const fieldValue = watch(name);

  // Use the custom hook for character count management
  const { characterCount, isNearLimit, isAtLimit, setCharacterCount } =
    useCharacterCount(fieldValue, maxLength);

  // Get the registered field
  const registeredField = register(name);

  // Handle textarea change to update character count
  const handleTextareaChange = (event) => {
    const value = event.target.value;
    setCharacterCount(value.length);

    // Call the original onChange from register
    if (registeredField.onChange) {
      registeredField.onChange(event);
    }
  };

  return (
    <Form.Field
      className={`${!labelAbove && "flex-between"} ${fieldStyle}`}
      error={!!error}
    >
      {label && (
        <label className={`${!labelAbove && "w-30"} ${labelStyle}`}>
          {label}
        </label>
      )}
      <div className={textareaContainerStyle}>
        <textarea
          {...registeredField}
          onChange={handleTextareaChange}
          rows={rows}
          placeholder={placeholder}
          readOnly={isInReadMode}
          maxLength={maxLength}
        />

        {showCharacterCount && (
          <div
            style={{
              textAlign: "right",
              fontSize: "0.85em",
              marginTop: "4px",
              color: isAtLimit
                ? "#db2828"
                : isNearLimit
                  ? "#f2711c"
                  : "#767676",
            }}
          >
            {characterCount}/{maxLength}
          </div>
        )}

        {error && (
          <Label pointing="above" prompt>
            {error?.message}
          </Label>
        )}
      </div>
    </Form.Field>
  );
}
